/* eslint-disable @typescript-eslint/indent */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import * as onboardingAPI from '../../api/onboardingAPI';
import * as contentAPI from '../../api/contentAPI';
import * as memberAPI from '../../api/memberAPI';
import { EnrollmentRequest, filterNullFields } from '../../common/models/EnrollmentRequest';
import { EnrollmentResponse } from '../../common/models/EnrollmentResponse';
import { Member } from '../../common/models/Member';
import { OnboardingForm } from '../../common/models/OnboardingForm';
import { RootState } from '../storeSetup';
import { CompanyRoleOptions } from '../../common/models/Question';
import { FormAnswer } from '../../common/models/FormAnswer';
import {
  EventObject, RequestAction, RequestEventDetailProps, RequestSubtopic,
} from '../../common/EventTrackingTypes';
import { ApiErrorResponse } from '../../common/models/ApiErrorResponse';
import { Logger } from '../../config/logging/logger';
import {
  SubscriptionStatus,
  SubscriptionTier,
  snMembershipLevelToSubscriptionTier,
} from '../../common/models/Subscription';

export interface ResponseError {
  // TODO: improve this interface to account for expected errors from API call
  errorMessage?: string
  apiResponseErrorStatus?: number,
}

export const getOnboardingForm = createAsyncThunk<
// Return type of the payload creator
OnboardingForm,
// First argument to the payload creator
{
  formId: string,
  isPublic?: boolean,
  trackRequest:(
    subtopic: RequestSubtopic,
    action: RequestAction,
    object: EventObject,
    errorEventDetailProps: RequestEventDetailProps) => void
    },
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
    'onboarding/getOnboardingForm',
    async ({ formId, isPublic, trackRequest }, { rejectWithValue, getState }) => {
      const logger = Logger.create('onboarding/getOnboardingForm');
      try {
        const response = isPublic ?
          await onboardingAPI.getPublicOnboardingForm(formId) :
          await onboardingAPI.getOnboardingForm(formId);
        return response as OnboardingForm;
      } catch (err) {
        logger.error(Logger.format(err));
        trackRequest(
          'Error',
          'Get',
          'OnboardingForm',
          {
            error: err,
            apiResponseErrorStatus: (err as AxiosError)?.response?.status,
          },
        );
        const responseError: ResponseError = { errorMessage: 'failed to get onboarding questions' };
        return rejectWithValue(responseError as ResponseError);
      }
    },
    );

export const getOnboardingFormAnswers = createAsyncThunk<
// Return type of the payload creator
FormAnswer[],
// First argument to the payload creator
{
  formId: string,
  trackRequest:(
    subtopic: RequestSubtopic,
    action: RequestAction,
    object: EventObject,
    errorEventDetailProps: RequestEventDetailProps,
  ) => void
    },
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
    'onboarding/getOnboardingFormAnswers',
    async ({ formId, trackRequest }, { rejectWithValue, getState }) => {
      const logger = Logger.create('onboarding/getOnboardingFormAnswers');
      const { memberSession: { memberId } } =
        getState().onboarding;
      try {
        const response = await contentAPI.getFormAnswers(formId, memberId);
        return response as FormAnswer[];
      } catch (err) {
        logger.error(Logger.format(err));
        trackRequest(
          'Error',
          'Get',
          'OnboardingFormAnswers',
          {
            error: err,
            apiResponseErrorStatus: (err as AxiosError)?.response?.status,
          },
        );
        const responseError: ResponseError = { errorMessage: 'failed to get onboarding answers' };
        return rejectWithValue(responseError as ResponseError);
      }
    },
    );

export const getMemberInformation = createAsyncThunk<
// Return type of the payload creator
Member,
// First argument to the payload creator
{
  id?: string,
  trackRequest:(
    subtopic: RequestSubtopic,
    action: RequestAction,
    object: EventObject,
    errorEventDetailProps: RequestEventDetailProps,
  ) => void
    },
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
    'onboarding/getMemberInformation',
    async ({ id, trackRequest }, { rejectWithValue, getState }) => {
      const logger = Logger.create('onboarding/getMemberInformation');
      const { memberSession } = getState().onboarding;
      const memberId = memberSession.memberId || id;
      const { solutionOrgId } = memberSession;
      const isSnSolutionOrg = solutionOrgId === process.env.REACT_APP_SERVICE_NATION_SOLUTION_ID;
      try {
        const member = await onboardingAPI.getMemberInformation(memberId) as Member;

        if (isSnSolutionOrg && member.email) {
          try {
            const snResponse = await memberAPI.getSnMembershipForSNNative(member.email);
            // isMasterAccount/myPurchasesAccess/historicRebateAccess only apply to the
            // native SN master/sub-account model, so they're only populated here for
            // is_sn_native members (not merely has_active_account members).
            if (snResponse.data?.is_sn_native) {
              const snTier = snResponse.data.membership_level?.trim().toLowerCase() ?? '';
              const resolvedTier: SubscriptionTier =
                snMembershipLevelToSubscriptionTier[snTier] ?? SubscriptionTier.EDGE;
              if (member.company) {
                member.company = {
                  ...member.company,
                  subscription: {
                    ...(member.company.subscription ?? {}),
                    subscriptionTier: resolvedTier,
                    subscriptionStatus: snResponse.data?.has_active_account ?
                      SubscriptionStatus.ACTIVE : SubscriptionStatus.INACTIVE,
                  } as typeof member.company.subscription,
                };
              }
              member.isMasterAccount = snResponse.data?.is_master_account;
              member.myPurchasesAccess = snResponse.data?.my_purchases_access;
              member.historicRebateAccess = snResponse.data?.historic_rebate_access;
            }
          } catch (snErr) {
            logger.error(Logger.format('Failed to fetch SN membership, using DB tier:', snErr));
          }
        }

        return member;
      } catch (err) {
        logger.error(Logger.format(err));
        trackRequest(
          'Error',
          'Get',
          'MemberInformation',
          {
            error: err,
            apiResponseErrorStatus: (err as AxiosError)?.response?.status,
          },
        );
        const responseError: ResponseError = { errorMessage: 'failed to get member information' };
        return rejectWithValue(responseError as ResponseError);
      }
    },
    );

export const getCompanyRoles = createAsyncThunk<
CompanyRoleOptions[],
{
  trackRequest:(
    subtopic: RequestSubtopic,
    action: RequestAction,
    object: EventObject,
    errorEventDetailProps: RequestEventDetailProps,
  ) => void
    },
{
  rejectValue: ResponseError;
  state: RootState
}>(
    'onboarding/getCompanyRoles',
    async ({ trackRequest }, { rejectWithValue, getState }) => {
      const logger = Logger.create('onboarding/getCompanyRoles');
      try {
        const response = await onboardingAPI.getCompanyRoles();
        return response as CompanyRoleOptions[];
      } catch (err) {
        logger.error(Logger.format(err));
        trackRequest(
          'Error',
          'Get',
          'CompanyRoles',
          {
            error: err,
            apiResponseErrorStatus: (err as AxiosError)?.response?.status,
          },
        );
        const responseError: ResponseError = { errorMessage: 'failed to get company roles' };
        return rejectWithValue(responseError as ResponseError);
      }
    },
    );

export const updateMemberInformation = createAsyncThunk<
// Return type of the payload creator
Member,
// First argument to the payload creator
{
  memberId: string;
  payload: Member;
},
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState;
}>(
  'onboarding/updateMemberInformation',
  async (request, { rejectWithValue }) => {
    try {
      const response =
          await onboardingAPI.updateMemberInformation(request.payload, request.memberId);
      return response as Member;
    } catch (err: unknown) {
      // TODO: handle request errors appropriately
      const responseError = ((err as AxiosError)?.response?.data ||
          { errorMessage: 'something went wrong' }) as ResponseError;
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const enrollMember = createAsyncThunk<
// Return type of the payload creator
EnrollmentResponse | ApiErrorResponse,
// First argument to the payload creator
{
  solutionOrgId: string;
  request: EnrollmentRequest;
  trackRequest:(
    subtopic: RequestSubtopic,
    action: RequestAction,
    object: EventObject,
    errorEventDetailProps: RequestEventDetailProps,
  ) => void
    },
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
    'onboarding/enrollMember',
    async ({ solutionOrgId, request, trackRequest }, { rejectWithValue, getState }) => {
      const logger = Logger.create('onboarding/enrollMember');
      try {
        // filter null field from EnrolledMember object
        request.member = filterNullFields(request.member);
        logger.debug(Logger.format(
          `enrollment request payload: ${JSON.stringify(request)}`,
        ));
        const response = await onboardingAPI.enrollMember(solutionOrgId, request);
        return response as EnrollmentResponse;
      } catch (err: unknown) {
        const apiErrorResponse = (err as AxiosError)?.response?.data as ApiErrorResponse;
        /**
           * @see OptimusErrors.MemberAlreadyEnrolled
           */
        if (apiErrorResponse.code === 'member_already_enrolled') {
          // Treat MemberAlreadyEnrolled Error as Promise.resolve()
          // so that the navigation can continue
          logger.debug(Logger.format(
            'OnboardingThunk:enrollMember:member_already_enrolled',
          ));
          return apiErrorResponse;
        }
        // send a rewards everpro signup failed event
        trackRequest(
          'Error',
          'Post',
          'EdgeEnrollmentSignup',
          {
            error: err,
            apiResponseErrorStatus: (err as AxiosError)?.response?.status,
          },
        );
        const responseError = apiErrorResponse ||
            { errorMessage: 'something went wrong' } as ResponseError;
        return rejectWithValue(responseError as ResponseError);
      }
    },
    );
